/**
 * @fileoverview Hook de integração central para DRE
 * @module hooks/useDREIntegration
 * 
 * @description
 * Hook que fornece acesso unificado aos dados da DRE para todas as páginas.
 * Garante sincronização reativa entre DRE Editável, Dashboard, Histórico e Premissas.
 * 
 * @features
 * - Acesso aos dados editáveis da DRE
 * - Indicador de edições vs dados originais
 * - Contagem precisa de campos editados
 * - Integração com ValuationEngine
 * - Cache e otimização de recálculos
 * 
 * @version 1.1.0 - Adicionado editCount
 * @date 2025-10-22
 */

import { useMemo } from 'react';
import { useDREStore } from '../stores/dreStore';
import type { DREData, DREYear } from '../types/dre';

// ============================================================================
// TYPES
// ============================================================================

export interface DREIntegrationData {
  /** Dados completos da DRE (editável) */
  dreData: DREData | null;
  
  /** Dados históricos originais (master/readonly) */
  masterData: Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'> | null;
  
  /** Indica se há dados editados diferentes do master */
  hasEdits: boolean;
  
  /** Número total de edições (campos modificados) em relação ao master */
  editCount: number;
  
  /** Indica se está em modo de edição */
  isEditing: boolean;
  
  /** Loading state */
  isLoading: boolean;
  
  /** Anos disponíveis por empresa */
  setimaYears: number[];
  studio555Years: number[];
  
  /** Último ano de cada empresa (para projeções) */
  setimaLastYear: DREYear | null;
  studio555LastYear: DREYear | null;
  
  /** Função para restaurar dados originais */
  restoreToOriginal: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook de integração central com DRE Store
 * 
 * @returns {DREIntegrationData} Dados consolidados da DRE
 * 
 * @example
 * ```tsx
 * function MyDashboard() {
 *   const { dreData, hasEdits, setimaLastYear } = useDREIntegration();
 *   
 *   if (!dreData) return <Loading />;
 *   
 *   return (
 *     <>
 *       {hasEdits && <Alert>Usando dados editados</Alert>}
 *       <Chart data={setimaLastYear} />
 *     </>
 *   );
 * }
 * ```
 */
export function useDREIntegration(): DREIntegrationData {
  const {
    dreData,
    masterData,
    isEditing,
    isLoading,
    restoreToOriginal,
  } = useDREStore();

  // Detectar se há edições diferentes do master
  const hasEdits = useMemo(() => {
    if (!dreData || !masterData) return false;
    
    // Comparação simples: verificar se os arrays têm tamanhos diferentes
    // ou se algum valor mudou
    const setimaEdited = 
      dreData.setima.years.length !== masterData.setima.years.length ||
      JSON.stringify(dreData.setima.years) !== JSON.stringify(masterData.setima.years);
    
    const studio555Edited = 
      dreData.studio555.years.length !== masterData.studio555.years.length ||
      JSON.stringify(dreData.studio555.years) !== JSON.stringify(masterData.studio555.years);
    
    return setimaEdited || studio555Edited;
  }, [dreData, masterData]);

  // Contar número de campos editados
  const editCount = useMemo(() => {
    if (!dreData || !masterData || !hasEdits) return 0;
    
    let count = 0;
    
    // Comparar anos da Sétima
    dreData.setima.years.forEach((currentYear, idx) => {
      const masterYear = masterData.setima.years[idx];
      if (!masterYear) {
        // Ano adicionado (conta como 1 edição)
        count += 1;
        return;
      }
      
      // Comparar cada campo editável do DRE
      const editableFields: (keyof DREYear)[] = [
        'receitaBruta',
        'impostosVendas',
        'custosDirectos',
        'despesasOperacionais',
        'depreciacao',
        'impostosLucro',
      ];
      
      editableFields.forEach(field => {
        if (currentYear[field] !== masterYear[field]) {
          count += 1;
        }
      });
    });
    
    // Comparar anos do 555
    dreData.studio555.years.forEach((currentYear, idx) => {
      const masterYear = masterData.studio555.years[idx];
      if (!masterYear) {
        count += 1;
        return;
      }
      
      const editableFields: (keyof DREYear)[] = [
        'receitaBruta',
        'impostosVendas',
        'custosDirectos',
        'despesasOperacionais',
        'depreciacao',
        'impostosLucro',
      ];
      
      editableFields.forEach(field => {
        if (currentYear[field] !== masterYear[field]) {
          count += 1;
        }
      });
    });
    
    return count;
  }, [dreData, masterData, hasEdits]);

  // Extrair anos disponíveis
  const setimaYears = useMemo(() => {
    if (!dreData) return [];
    return dreData.setima.years.map(y => y.year).sort((a, b) => a - b);
  }, [dreData]);

  const studio555Years = useMemo(() => {
    if (!dreData) return [];
    return dreData.studio555.years.map(y => y.year).sort((a, b) => a - b);
  }, [dreData]);

  // Obter último ano (mais recente) de cada empresa
  const setimaLastYear = useMemo(() => {
    if (!dreData || setimaYears.length === 0) return null;
    const lastYear = Math.max(...setimaYears);
    return dreData.setima.years.find(y => y.year === lastYear) || null;
  }, [dreData, setimaYears]);

  const studio555LastYear = useMemo(() => {
    if (!dreData || studio555Years.length === 0) return null;
    const lastYear = Math.max(...studio555Years);
    return dreData.studio555.years.find(y => y.year === lastYear) || null;
  }, [dreData, studio555Years]);

  return {
    dreData,
    masterData,
    hasEdits,
    editCount,
    isEditing,
    isLoading,
    setimaYears,
    studio555Years,
    setimaLastYear,
    studio555LastYear,
    restoreToOriginal,
  };
}

/**
 * Hook auxiliar para obter último ano histórico de uma empresa
 * 
 * @param company - 'setima' ou '555'
 * @returns {DREYear | null} Último ano disponível
 * 
 * @example
 * ```tsx
 * const lastYearSetima = useLastHistoricalYear('setima');
 * const taxaCrescimento = calculateGrowthRate(lastYearSetima);
 * ```
 */
export function useLastHistoricalYear(company: 'setima' | '555'): DREYear | null {
  const { setimaLastYear, studio555LastYear } = useDREIntegration();
  return company === 'setima' ? setimaLastYear : studio555LastYear;
}

/**
 * Hook auxiliar para verificar se há edições pendentes
 * 
 * @returns {boolean} True se DRE foi editada
 * 
 * @example
 * ```tsx
 * function WarningBanner() {
 *   const hasEdits = useHasDREEdits();
 *   if (!hasEdits) return null;
 *   return <Alert>DRE editada - salve para persistir</Alert>;
 * }
 * ```
 */
export function useHasDREEdits(): boolean {
  const { hasEdits } = useDREIntegration();
  return hasEdits;
}
